
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faEnvelope, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faGithub, faTwitter } from "@fortawesome/free-brands-svg-icons";
import { Col, Row, Form, Card, Button, FormCheck, Container, InputGroup, Image, Alert } from '@themesberg/react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";

import { Routes } from "../../routes";
import BgImage from "../../assets/img/illustrations/signin.svg";
import ProductivosLogo from "../../assets/img/productivos-logo.png";
import { useDispatch, useSelector } from "react-redux";
import { clearRedirect, login, clearError } from "../../redux/authSlice";


function Login() {
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.auth);
  const navigate = useHistory();
  const location = useLocation();
  const redirectPath = useSelector((state) => state.auth.redirectPath);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [registrationMessage, setRegistrationMessage] = useState(null);
  const [registrationError, setRegistrationError] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    // All validation is already done by the disabled state
    // Just dispatch the login action
    dispatch(login(username, password));
  };

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (captchaError) {
      setCaptchaError('');
    }
  };

  useEffect(() => {
    // Check for registration messages in URL params
    const params = new URLSearchParams(location.search);
    const successMsg = params.get('success');
    const errorMsg = params.get('error');

    if (successMsg) {
      setRegistrationMessage(decodeURIComponent(successMsg));
    }
    if (errorMsg) {
      setRegistrationError(decodeURIComponent(errorMsg));
    }
  }, [location]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate.push(redirectPath);
      dispatch(clearRedirect()); // Limpiar la ruta después de redirigir
    }
  }, [isAuthenticated, navigate, dispatch, redirectPath]);

  // Check if form is valid (all fields filled + CAPTCHA completed)
  const isFormValid = username.trim() !== '' && password.trim() !== '' && captchaValue !== null;

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <p className="text-center">
            <Card.Link as={Link} to={Routes.DashboardOverview.path} className="text-gray-700">
              <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Volver a la pagina principal
            </Card.Link>
          </p>
          <Row className="justify-content-center form-bg-image" style={{ backgroundImage: `url(${BgImage})` }}>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <Image src={ProductivosLogo} alt="Productivos.org" style={{ height: '60px', marginBottom: '20px' }} />
                </div>

                {registrationMessage && (
                  <Alert variant="success" onClose={() => setRegistrationMessage(null)} dismissible>
                    <strong>¡Usuario creado exitosamente!</strong>
                    <p className="mb-0 mt-2">{registrationMessage}</p>
                  </Alert>
                )}

                {registrationError && (
                  <Alert variant="danger" onClose={() => setRegistrationError(null)} dismissible>
                    <strong>Error en el registro</strong>
                    <p className="mb-0 mt-2">{registrationError}</p>
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger">
                    <strong>Error de inicio de sesión</strong>
                    <p className="mb-0 mt-2">{error}</p>
                  </Alert>
                )}
                <Form className="mt-4" onSubmit={handleSubmit}>
                  <Form.Group id="email" className="mb-4">
                    <Form.Label>Usuario Productivos</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faEnvelope} />
                      </InputGroup.Text>
                      <Form.Control autoFocus required type="text" placeholder="example@company.com"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          if (error) dispatch(clearError());
                        }} />
                    </InputGroup>
                  </Form.Group>
                  <Form.Group>
                    <Form.Group id="password" className="mb-4">
                      <Form.Label>Contraseña</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control required type="password" placeholder="Password" value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (error) dispatch(clearError());
                          }} />
                      </InputGroup>
                    </Form.Group>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Form.Check type="checkbox">
                        <FormCheck.Input id="defaultCheck5" className="me-2" />
                        <FormCheck.Label htmlFor="defaultCheck5" className="mb-0">Recordarme</FormCheck.Label>
                      </Form.Check>
                      <Card.Link as={Link} to={Routes.ForgotPassword.path} className="small text-end">¿Olvidó de contraseña?</Card.Link>
                    </div>
                  </Form.Group>

                  {/* reCAPTCHA */}
                  <Form.Group className="mb-3 d-flex justify-content-center">
                    <div>
                      <ReCAPTCHA
                        sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                        onChange={onCaptchaChange}
                      />
                      {captchaError && (
                        <div className="text-danger small mt-1 text-center">
                          {captchaError}
                        </div>
                      )}
                    </div>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={!isFormValid || loading}
                  >
                    {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                  </Button>
                </Form>

                <div className="d-flex justify-content-center align-items-center mt-4">
                  <span className="fw-normal">
                    No estas registrado?
                    <Card.Link as={Link} to={Routes.Signup.path} className="fw-bold">
                      {` Crearme una cuenta `}
                    </Card.Link>
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
}

export default Login;
